import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { woocommerceApi, formatWooPrice } from '@/lib/api/services/woocommerce';
import {
    buildWooProductPayload,
    EMPTY_WOO_PRODUCT_FORM,
    useWooProductForm,
    WooProductFormFields,
    type WooImageItem,
} from './wooProductForm';
import { toast } from '@/hooks/use-toast';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { Loader2, RefreshCw, Eye, Pencil, ExternalLink, Package, MoreVerticalIcon, ImagePlus, X, Trash2 } from 'lucide-react';
import { useCatalogInfo } from '@/hooks/useCommerce';
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';

interface WooCommerceProductsProps {
    workspaceScopeId: string | null;
    isConnected: boolean;
    currency?: string;
    onAddProduct: () => void;
}

function parseJson<T>(value: any, fallback: T): T {
    if (!value) return fallback;
    if (typeof value === 'object') return value as T;
    try {
        return JSON.parse(value) as T;
    } catch {
        return fallback;
    }
}

function getFirstImage(images: any): string | null {
    const arr = parseJson<any[]>(images, []);
    return arr?.[0]?.src || arr?.[0]?.thumbnail || null;
}

function stripHtml(html: string): string {
    return html?.replace(/<[^>]+>/g, '').trim() || '';
}

interface ViewDetailsModalProps {
    product: any | null;
    currency: string;
    onClose: () => void;
}

const ViewDetailsModal: React.FC<ViewDetailsModalProps> = ({ product, currency, onClose }) => {
    if (!product) return null;

    const images = parseJson<any[]>(product.images, []);
    const wooData = parseJson<any>(product.woocommerce_data, {});
    const categories = parseJson<any[]>(product.categories, []);
    const tags = parseJson<any[]>(product.tags, []);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg">{product.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {images.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {images.map((img: any, i: number) => (
                                <img
                                    key={i}
                                    src={img.src}
                                    alt={img.alt || product.name}
                                    className="w-32 h-32 object-cover rounded-md border"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div>
                            <span className="font-medium text-muted-foreground">Status</span>
                            <div>
                                <Badge variant={product.status === 'publish' ? 'default' : 'secondary'}>
                                    {product.status}
                                </Badge>
                            </div>
                        </div>
                        <div>
                            <span className="font-medium text-muted-foreground">Type</span>
                            <div className="capitalize">{product.type || '—'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-muted-foreground">Regular Price</span>
                            <div>{product.regular_price ? formatWooPrice(Number(product.regular_price), currency) : '—'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-muted-foreground">Sale Price</span>
                            <div className={product.sale_price ? 'text-green-600 font-medium' : ''}>
                                {product.sale_price ? formatWooPrice(Number(product.sale_price), currency) : '—'}
                            </div>
                        </div>
                        <div>
                            <span className="font-medium text-muted-foreground">SKU</span>
                            <div>{product.sku || '—'}</div>
                        </div>
                        <div>
                            <span className="font-medium text-muted-foreground">Stock</span>
                            <div className={product.stock_status === 'instock' ? 'text-green-600' : 'text-red-600'}>
                                {product.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                                {product.stock_quantity != null && (
                                    <span className="text-muted-foreground ml-1">({product.stock_quantity})</span>
                                )}
                            </div>
                        </div>
                        {categories.length > 0 && (
                            <div className="col-span-2">
                                <span className="font-medium text-muted-foreground">Categories</span>
                                <div className="flex gap-1 flex-wrap mt-1">
                                    {categories.map((c: any, i: number) => (
                                        <Badge key={i} variant="outline" className="text-xs">{c.name}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {tags.length > 0 && (
                            <div className="col-span-2">
                                <span className="font-medium text-muted-foreground">Tags</span>
                                <div className="flex gap-1 flex-wrap mt-1">
                                    {tags.map((t: any, i: number) => (
                                        <Badge key={i} variant="secondary" className="text-xs">{t.name}</Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {product.short_description && (
                        <div>
                            <p className="font-medium text-sm text-muted-foreground mb-1">Short Description</p>
                            <p className="text-sm">{stripHtml(product.short_description)}</p>
                        </div>
                    )}
                    {product.description && (
                        <div>
                            <p className="font-medium text-sm text-muted-foreground mb-1">Description</p>
                            <p className="text-sm text-muted-foreground">{stripHtml(product.description)}</p>
                        </div>
                    )}

                    {wooData?.permalink && (
                        <div>
                            <a
                                href={wooData.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View on store
                            </a>
                        </div>
                    )}

                    <div className="flex flex-col gap-0.5 text-xs text-muted-foreground border-t pt-3">
                        <span>WooCommerce ID: #{product.woocommerce_product_id}</span>
                        <span>Last synced: {product.updated_at ? new Date(product.updated_at).toLocaleString() : '—'}</span>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

interface AddProductModalProps {
    currency: string;
    businessId: string;
    onClose: () => void;
    onCreated: () => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ currency, businessId, onClose, onCreated }) => {
    const form = useWooProductForm({ open: true, businessId, currency });

    const handleCreate = async () => {
        if ((await form.submit()).ok) onCreated();
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Add Product</DialogTitle>
                </DialogHeader>
                <WooProductFormFields
                    form={form.value}
                    currency={currency}
                    onChange={form.set}
                    images={form.images}
                    onImagesChange={form.setImages}
                    businessId={businessId}
                />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={form.loading}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={form.loading || form.uploading}>
                        {form.loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Create Product
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

interface EditProductModalProps {
    product: any | null;
    currency: string;
    businessId: string;
    onClose: () => void;
    onSaved: (updated: any) => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({ product, currency, businessId, onClose, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        ...EMPTY_WOO_PRODUCT_FORM,
        name: product?.name || '',
        regular_price: String(product?.regular_price || ''),
        sale_price: String(product?.sale_price || ''),
        sku: product?.sku || '',
        status: product?.status || 'publish',
        stock_status: product?.stock_status || 'instock',
        stock_quantity: product?.stock_quantity != null ? String(product.stock_quantity) : '',
        short_description: stripHtml(product?.short_description || ''),
        category: parseJson<Array<{ name?: string }>>(product?.categories, [])[0]?.name || '',
    });
    const [images, setImages] = useState<WooImageItem[]>(() => {
        const existing = parseJson<any[]>(product?.images, []);
        return existing.map((img: any) => ({ url: img.src || img.thumbnail || '' })).filter(img => img.url);
    });

    if (!product) return null;

    const set = (key: string, val: string) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        if (images.some(img => img.uploading)) {
            toast({ title: 'Please wait', description: 'Images are still uploading.', variant: 'warning' });
            return;
        }
        setSaving(true);
        try {
            const updates = buildWooProductPayload(form, images);
            const res = await woocommerceApi.updateProduct(businessId, product.woocommerce_product_id, updates);
            toast({ variant: "success", title: 'Product Updated', description: `${form.name} saved successfully.` });
            onSaved(res.product || res);
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Update Failed' } });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Edit Product</DialogTitle>
                </DialogHeader>
                <WooProductFormFields
                    form={form}
                    currency={currency}
                    onChange={set}
                    images={images}
                    onImagesChange={setImages}
                    businessId={businessId}
                />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={saving || images.some(img => img.uploading)}>
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const WooCommerceProducts: React.FC<WooCommerceProductsProps> = ({
    workspaceScopeId,
    isConnected,
    currency = 'USD',
    onAddProduct,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: catalogInfo } = useCatalogInfo();

    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [syncing, setSyncing] = useState(false);
    const [syncToCatalogLoading, setSyncToCatalogLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewProduct, setViewProduct] = useState<any | null>(null);
    const [editProduct, setEditProduct] = useState<any | null>(null);
    const [addingProduct, setAddingProduct] = useState(false);
    
    const [productToDelete, setProductToDelete] = useState<any | null>(null);
    const [deleteProductLoading, setDeleteProductLoading] = useState(false);

    const page = parseInt(searchParams.get('wooProdPage') || '1');
    const setPage = (p: number) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (p > 1) newParams.set('wooProdPage', p.toString());
            else newParams.delete('wooProdPage');
            return newParams;
        });
    };
    const pageSize = parseInt(searchParams.get('wooProdPageSize') || '10');
    const setPageSize = (s: number) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (s !== 10) newParams.set('wooProdPageSize', s.toString());
            else newParams.delete('wooProdPageSize');
            newParams.delete('wooProdPage');
            return newParams;
        });
    };

    useEffect(() => {
        if (isConnected && workspaceScopeId) {
            loadProducts();
        }
    }, [isConnected, workspaceScopeId]);

    const loadProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await woocommerceApi.getLocalProducts(workspaceScopeId!, 500, 0);
            setProducts(res.products || []);
        } catch (e: any) {
            setError(friendlyMessage(e, 'Failed to load products'));
        } finally {
            setLoading(false);
            markLoaded();
        }
    };

    const handleSyncProducts = async () => {
        if (!workspaceScopeId) return;
        setSyncing(true);
        try {
            const res = await woocommerceApi.syncProducts(workspaceScopeId, 100);
            const count = res.synced || 0;
            toast(count > 0
                ? { title: 'Synced', description: `Synced ${count} ${count === 1 ? 'product' : 'products'}.` }
                : { title: 'Up to date', description: 'No new products to sync.' }
            );
            loadProducts();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Sync Failed' } });
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncToCatalog = async () => {
        if (!workspaceScopeId) return;
        const catalogStatusKnown = typeof catalogInfo !== 'undefined';
        setSyncToCatalogLoading(true);
        try {
            await woocommerceApi.syncToCatalog(workspaceScopeId, workspaceScopeId, {
                limit: 100,
                skipExisting: true,
                catalogId: catalogInfo?.whatsapp_catalog_id || catalogInfo?.id,
            });
            toast({ variant: "success", title: 'Catalog Sync Started', description: 'Sync started in background.' });
        } catch (e: any) {
            let errorMsg = e.message || 'Failed to sync to catalog';
            if (errorMsg.includes('catalog not connected')) errorMsg = 'WhatsApp catalog is not connected.';
            toast({ title: 'Sync Failed', description: errorMsg, variant: 'destructive' });
        } finally {
            setSyncToCatalogLoading(false);
        }
    };

    const handleProductSaved = (updated: any) => {
        loadProducts();
        setEditProduct(null);
    };

    const handleDeleteProduct = async () => {
        if (!workspaceScopeId || !productToDelete) return;
        setDeleteProductLoading(true);
        try {
            await woocommerceApi.deleteProduct(workspaceScopeId, productToDelete.woocommerce_product_id);
            toast({ variant: "success", title: 'Product Deleted', description: 'The product was successfully deleted.' });
            loadProducts();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Delete Failed' } });
        } finally {
            setDeleteProductLoading(false);
            setProductToDelete(null);
        }
    };

    const columns: Column<any>[] = useMemo(() => [
        {
            key: 'image',
            header: 'Image',
            render: (_, p) => {
                const src = getFirstImage(p.images);
                return src ? (
                    <img
                        src={src}
                        alt={p.name}
                        className="w-12 h-12 object-cover rounded-md border"
                        onError={(e) => {
                            (e.target as HTMLImageElement).replaceWith(
                                Object.assign(document.createElement('div'), {
                                    className: 'w-12 h-12 rounded-md border bg-muted flex items-center justify-center',
                                    innerHTML: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="20" x="2" y="2" rx="2"/><path d="m9 9 6 6"/><path d="m15 9-6 6"/></svg>',
                                })
                            );
                        }}
                    />
                ) : (
                    <div className="w-12 h-12 rounded-md border bg-muted flex items-center justify-center">
                        <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                );
            },
        },
        {
            key: 'name',
            header: 'Product Name',
            render: (_, p) => (
                <div>
                    <div className="font-medium text-sm">{p.name}</div>
                    {p.sku && <div className="text-xs text-muted-foreground">SKU: {p.sku}</div>}
                    <div className="text-xs text-muted-foreground">#{p.woocommerce_product_id}</div>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (_, p) => (
                <Badge variant={p.status === 'publish' ? 'default' : 'secondary'} className="text-xs">
                    {p.status}
                </Badge>
            ),
        },
        {
            key: 'price',
            header: 'Price',
            render: (_, p) => (
                <div className="text-sm">
                    {p.sale_price ? (
                        <>
                            <span className="line-through text-muted-foreground text-xs mr-1">
                                {formatWooPrice(Number(p.regular_price || 0), currency)}
                            </span>
                            <span className="text-green-600 font-medium">
                                {formatWooPrice(Number(p.sale_price), currency)}
                            </span>
                        </>
                    ) : (
                        formatWooPrice(Number(p.price || 0), currency)
                    )}
                </div>
            ),
        },
        {
            key: 'stock',
            header: 'Stock',
            render: (_, p) => (
                <div className={`text-sm ${p.stock_status === 'instock' ? 'text-green-600' : 'text-red-600'}`}>
                    {p.stock_status === 'instock' ? 'In Stock' : 'Out of Stock'}
                    {p.stock_quantity != null && (
                        <span className="text-muted-foreground ml-1">({p.stock_quantity})</span>
                    )}
                </div>
            ),
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_, p) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreVerticalIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewProduct(p);
                            }}
                        >
                            <Eye className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditProduct(p);
                            }}
                        >
                            <Pencil className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation();
                                setProductToDelete(p);
                            }}
                            className="text-destructive group"
                        >
                            <Trash2 className="mr-2 h-3 w-3 sm:h-4 sm:w-4 group-hover:text-destructive" />
                            Delete
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {(() => {
                            const wooData = parseJson<any>(p.woocommerce_data, {});
                            return wooData?.permalink ? (
                                <DropdownMenuItem asChild>
                                    <a
                                        href={wooData.permalink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <ExternalLink className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                                        View on Store
                                    </a>
                                </DropdownMenuItem>
                            ) : null;
                        })()}
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ], [currency]);

    return (
        <div className="space-y-4">
            {error && <div className="p-3 bg-destructive/10 text-destructive text-sm rounded">{error}</div>}

            <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                    {products.length > 0 ? `${products.length} product${products.length !== 1 ? 's' : ''}` : ''}
                </p>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleSyncProducts} disabled={syncing}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                        Sync WooCommerce
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSyncToCatalog} disabled={syncToCatalogLoading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${syncToCatalogLoading ? 'animate-spin' : ''}`} />
                        Sync to Catalog
                    </Button>
                    <Button size="sm" onClick={() => setAddingProduct(true)}>
                        <span className="mr-2">+</span> Add Product
                    </Button>
                </div>
            </div>

            <div className="rounded-md border">
                <DataTable
                    columns={columns}
                    data={products}
                    currentPage={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={size => { setPageSize(size); setPage(1); }}
                    totalItems={products.length}
                    loading={showListSkeleton({ loading, hasLoaded, willFetch: isConnected && !!workspaceScopeId })}
                    emptyMessage="No products found. Click 'Sync WooCommerce' to fetch your products."
                    showPagination={true}
                    pageSizeOptions={[10, 20, 50]}
                />
            </div>

            {addingProduct && workspaceScopeId && (
                <AddProductModal
                    currency={currency}
                    businessId={workspaceScopeId}
                    onClose={() => setAddingProduct(false)}
                    onCreated={() => { setAddingProduct(false); loadProducts(); }}
                />
            )}

            {viewProduct && (
                <ViewDetailsModal
                    product={viewProduct}
                    currency={currency}
                    onClose={() => setViewProduct(null)}
                />
            )}

            {editProduct && workspaceScopeId && (
                <EditProductModal
                    product={editProduct}
                    currency={currency}
                    businessId={workspaceScopeId}
                    onClose={() => setEditProduct(null)}
                    onSaved={handleProductSaved}
                />
            )}

            <AlertDialog open={!!productToDelete} onOpenChange={(open) => !open && setProductToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete "{productToDelete?.name}" from your WooCommerce store. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteProductLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteProduct();
                            }}
                            disabled={deleteProductLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteProductLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete Product
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
